import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import { clearRoleCaches, SIGNED_OUT_FLAG } from "@/hooks/use-auth";

/**
 * Vigilante global de sesión (uno solo en toda la app).
 *
 * Cuando la sesión expira (el refresh token deja de ser válido) Supabase emite
 * `SIGNED_OUT`. Sin este listener, el usuario se queda en una pantalla
 * protegida con datos en caché y peticiones que devuelven 401, y solo se
 * entera al navegar. Aquí:
 *  - limpiamos la caché de datos protegidos y la caché de roles,
 *  - si está en una ruta protegida lo mandamos a /auth con `next` para que
 *    vuelva exactamente a donde estaba tras autenticarse,
 *  - si está en una ruta pública solo revalidamos el router.
 */
import { isSessionPrivatePath as isProtectedPath } from "@/lib/private-paths";

/** ¿Queda algún token de sesión guardado en este navegador? */
function hasStoredSession() {
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) return true;
    }
  } catch {
    return true; // sin acceso al almacenamiento: deja decidir al guard normal
  }
  return false;
}

function kickIfSignedOut() {
  if (isProtectedPath(window.location.pathname) && !hasStoredSession()) {
    document.documentElement.style.visibility = "hidden";
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/auth?next=${next}`);
    return true;
  }
  return false;
}

export function AuthSessionWatcher() {
  const router = useRouter();
  const queryClient = useQueryClient();

  // Mensaje "Cerraste sesión" tras la recarga, y protección del botón Atrás:
  // si el navegador restaura una página privada desde su caché, se recarga
  // para que el guard de sesión la bloquee.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(SIGNED_OUT_FLAG)) {
        window.sessionStorage.removeItem(SIGNED_OUT_FLAG);
        void import("sonner").then(({ toast }) => toast.success("Cerraste sesión"));
      }
    } catch {
      /* ignore */
    }
    kickIfSignedOut();
    const onShow = (e: PageTransitionEvent) => {
      if (kickIfSignedOut()) return;
      if (e.persisted && isProtectedPath(window.location.pathname)) window.location.reload();
    };
    // Atrás/adelante dentro de la app y regreso a una pestaña abierta.
    const onPop = () => void kickIfSignedOut();
    const onVisible = () => {
      if (document.visibilityState === "visible") kickIfSignedOut();
    };
    // Otra pestaña cerró sesión.
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.startsWith("sb-") && e.newValue === null) kickIfSignedOut();
    };
    window.addEventListener("pageshow", onShow);
    window.addEventListener("popstate", onPop);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;

    void (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      if (!mounted) return;
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === "SIGNED_OUT") {
          void queryClient.cancelQueries();
          queryClient.clear();
          clearRoleCaches();
          const path = window.location.pathname;
          if (isProtectedPath(path)) {
            const next = encodeURIComponent(path + window.location.search);
            window.location.replace(`/auth?next=${next}`);
            return;
          }
          void router.invalidate();
          return;
        }
        if (event === "SIGNED_IN" || event === "USER_UPDATED") {
          if (!session) return;
          void router.invalidate();
          void queryClient.invalidateQueries();
        }
      });
      unsubscribe = () => data.subscription.unsubscribe();
      if (!mounted) unsubscribe();
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [router, queryClient]);

  return null;
}
